import { Alert, AlertDescription, AlertTitle } from '@shared/components/ui/shadcn/alert';
import { useAuthStore } from '@stores/authStore';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMissingDefaultShippingCosts, useProductsWithoutCost, useProfitabilityAccess } from '../../hooks';

export const ProfitabilitySetupAlerts = () => {
  const { t } = useTranslation('profitabilityModule');
  const canConfigureProductCosts = useProfitabilityAccess();
  const canConfigureShippingCosts = useAuthStore(
    (s) => s.getTeamType() === 'tenant_external' && s.getProfitabilityEnabled(),
  );

  const productsQuery = useProductsWithoutCost(canConfigureProductCosts, { page: 1, per_page: 1 });
  const shippingQuery = useMissingDefaultShippingCosts(canConfigureShippingCosts, { page: 1, per_page: 1 });

  const productsCount = productsQuery.data?.meta?.total ?? 0;
  const shippingCount = shippingQuery.data?.meta?.total ?? 0;
  const isLoading =
    (canConfigureProductCosts && productsQuery.isLoading) || (canConfigureShippingCosts && shippingQuery.isLoading);

  if (isLoading || (productsCount === 0 && shippingCount === 0)) {
    return null;
  }

  return (
    <div className="space-y-3" data-testid="profitability-setup-alerts">
      {productsCount > 0 && (
        <Alert className="border-amber-500/40 bg-amber-500/5" data-testid="profitability-alert-products-without-cost">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <AlertTitle className="text-foreground">{t('setup_alerts.products_title')}</AlertTitle>
          <AlertDescription className="text-muted-foreground">
            {t('setup_alerts.products_description', { count: productsCount })}{' '}
            <Link
              to="/reports/profitability/product-costs"
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              {t('setup_alerts.manage')}
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {shippingCount > 0 && (
        <Alert className="border-amber-500/40 bg-amber-500/5" data-testid="profitability-alert-shipping-without-cost">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <AlertTitle className="text-foreground">{t('setup_alerts.shipping_title')}</AlertTitle>
          <AlertDescription className="text-muted-foreground">
            {t('setup_alerts.shipping_description', { count: shippingCount })}{' '}
            <Link
              to="/reports/profitability/shipping-costs"
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              {t('setup_alerts.manage')}
            </Link>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
